import * as React from 'react';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * UserMenu — profile-icon dropdown for the right cluster of UniversalTopNav.
 *
 *   <UserMenu
 *     name="Sipho Mthethwa"
 *     email="sipho@example.co.za"
 *     items={[
 *       { label: 'Profile settings', onSelect: () => nav('/app/settings') },
 *       { label: 'Sign out', tone: 'danger', onSelect: signOut, separated: true },
 *     ]}
 *   />
 *
 * Renders a circular avatar (initials when no `src`) plus a chevron, opening
 * a dropdown that shows the user's name + email header followed by the items.
 */

export interface UserMenuItem {
  label: React.ReactNode;
  onSelect?: () => void;
  href?: string;
  disabled?: boolean;
  /** `danger` = destructive (red text). */
  tone?: 'default' | 'danger';
  /** Render a divider above this item. */
  separated?: boolean;
  /** Optional left icon — Lucide component rendered at size-4. */
  icon?: React.ComponentType<{ className?: string }>;
}

interface UserMenuProps {
  /** Full display name shown in the dropdown header. */
  name: string;
  /** Optional email shown beneath the name. */
  email?: string;
  /** Avatar image URL — falls back to initials when omitted or load fails. */
  avatarSrc?: string;
  /** Override the auto-derived initials. */
  initials?: string;
  /** Menu items. Per-item `tone='danger'` flips the text colour. */
  items: UserMenuItem[];
  /** ARIA label for the trigger button. Defaults to "Open user menu". */
  ariaLabel?: string;
  className?: string;
}

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function UserMenu({
  name,
  email,
  avatarSrc,
  initials,
  items,
  ariaLabel = 'Open user menu',
  className,
}: UserMenuProps) {
  const fallback = initials ?? deriveInitials(name);

  return (
    <DropdownPrimitive.Root>
      <DropdownPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full p-1 pr-1.5 text-sm text-white',
            'hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40',
            className
          )}
        >
          {/* size-9 (36px) + p-1 = a 44px trigger — exactly the WCAG 2.5.8
              minimum touch target. The previous size-12 (48px) avatar made a
              56px control inside an h-16 (64px) top bar, leaving 4px of slack
              per side and squeezing the nav into a horizontal scrollbar. */}
          <span className="inline-flex size-9 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground ring-2 ring-white/40 shadow-md overflow-hidden">
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="h-full w-full object-cover" />
            ) : (
              fallback
            )}
          </span>
          <ChevronDown className="size-4 text-white/80" aria-hidden />
        </button>
      </DropdownPrimitive.Trigger>
      <DropdownPrimitive.Portal>
        <DropdownPrimitive.Content
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className={cn(
            // Themed popover surface. This was 'bg-amber-700' + white text — a
            // workaround for a JIT purge problem ("no arbitrary values that risk
            // getting purged"), not a design decision. The purge bug is gone
            // (globals.css @source-registers this package) and the popover
            // tokens are verified in the built CSS, so the panel can simply be
            // a surface again and follow the theme like everything else.
            'z-[60] min-w-[240px] rounded-md p-1',
            'bg-popover text-popover-foreground',
            'border border-border-strong ring-1 ring-black/5 shadow-popover',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        >
          <div className="px-3 py-2">
            <p className="text-sm font-semibold text-foreground truncate">{name}</p>
            {email && <p className="text-xs text-muted-foreground truncate">{email}</p>}
          </div>
          <DropdownPrimitive.Separator className="my-1 h-px bg-border" />
          {items.map((item, i) => {
            const Icon = item.icon;
            const itemClass = cn(
              'flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none transition-colors',
              'data-[highlighted]:bg-card-hover',
              item.tone === 'danger'
                ? 'text-danger-dark'
                : 'text-foreground',
              item.disabled && 'opacity-50 pointer-events-none'
            );
            return (
              <React.Fragment key={i}>
                {item.separated && i > 0 && (
                  <DropdownPrimitive.Separator className="my-1 h-px bg-border" />
                )}
                {item.href ? (
                  <DropdownPrimitive.Item asChild disabled={item.disabled}>
                    <a href={item.href} className={itemClass}>
                      {Icon && <Icon className="size-4" aria-hidden />}
                      <span>{item.label}</span>
                    </a>
                  </DropdownPrimitive.Item>
                ) : (
                  <DropdownPrimitive.Item
                    disabled={item.disabled}
                    onSelect={() => item.onSelect?.()}
                    className={itemClass}
                  >
                    {Icon && <Icon className="size-4" aria-hidden />}
                    <span>{item.label}</span>
                  </DropdownPrimitive.Item>
                )}
              </React.Fragment>
            );
          })}
        </DropdownPrimitive.Content>
      </DropdownPrimitive.Portal>
    </DropdownPrimitive.Root>
  );
}
